import { createSelector } from '@reduxjs/toolkit';
import { Box3, Euler, Matrix4, Vector3 } from 'three';

import {
  AnalogJoint,
  AnalogLink,
  AnalogModel,
  AnalogPanel,
  BasicProfile,
  Bearing,
  BearingEnvelope,
  Building,
  CalculatedLine,
  Component,
  ComponentDesign,
  Insertion,
  Level,
  Member,
  PlatePair,
  Point2D as Point2DInterface,
  Point3D,
  ProfileJoint,
  RoofContainer,
} from '@paragon/contract-library';
import { convertDegreesToRadians, Direction2D, distanceEquals, Line2D, Point2D } from '@paragon/geometry-library';
import {
  calculateBearingEnvelopeShape,
  calculateTrussEnvelopeShape,
  calculateTrussSolidWithCache,
  TrussSolidCalculationDependencies,
} from '@paragon/layout';
import { getInsertionsWithPendingChanges } from '@paragon/layout-models';
import { calculateLine, calculateMemberGeometry } from '@paragon/relative-geometry';
import {
  calculateMemberPolygon,
  getBoundsFromPoints,
  getPanelStartAndEndPoints,
  mergeBounds,
  SceneObjectBounds,
} from '@paragon/three-shared';
import { max, notNullish, ReadonlyRecord } from '@paragon/utilities';

import { getTrussSolidCalculationDependencies } from '../../library/layout-geometry/truss-envelopes/trussSolidSelectors';
import {
  calculateWebJointSegmentGeometry,
  findAndCalculateWebJointLocation,
} from '../../library/profiles/profileManipulation';
import { SelectableItemEntry } from '../../library/selection/types';
import { RootState } from '..';
import { getBearingVisualData } from './bearingGeometrySelectors';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getActiveComponent } from './getActiveComponentSelector';
import { getActiveDimensionSet } from './getActiveDimensionSet';
import { getReferenceLinesForActiveComponent } from './getReferenceLinesForActiveComponent';
import { getSelectedItemEntries, getSingleSelectedItemEntryOrNull } from './getSelectedItemSelector';
import { getAllBuildingsDictionary, getAllLevelsDictionary } from './layoutDataSelectors';
import { isLayoutCameraInUse } from './layoutModeSelectors';
import { getInsertingOrRewebbingProfileOrNull, getInsertingProfileTrussSettingsOrNull } from './profileSelectors';

const getBoundsFromMinMax = (min: Point2DInterface, max: Point2DInterface) => {
  const x = (min.x + max.x) / 2;
  const y = (min.y + max.y) / 2;

  const width = Math.abs(max.x - min.x);
  const height = Math.abs(max.y - min.y);

  return {
    position: { x, y },
    size: { width, height },
  };
};

export const calculateMemberBounds = (component: Component, member: Member) => {
  const memberPolygon = calculateMemberPolygon(member, component);
  if (!memberPolygon) return null;
  return getBoundsFromPoints(memberPolygon.vertices);
};

export const calculateComponentBounds = (
  component: Component,
  includeDimensions: boolean,
  dimensionBounds: DimensionBounds | null,
) => {
  const memberPoints: Point2DInterface[] = component.members.flatMap((member) => {
    const memberPolygon = calculateMemberPolygon(member, component);
    if (!memberPolygon) return [];
    return memberPolygon.vertices;
  });

  const bearingPoints: Point2DInterface[] = component.bearings.flatMap((bearing) => {
    const bearingVisualData = getBearingVisualData(component, bearing);
    if (!bearingVisualData) return [];
    const { position, width, height } = bearingVisualData;
    return [
      { x: position.x - width / 2, y: position.y },
      { x: position.x + width / 2, y: position.y },
      { x: position.x + width / 2, y: position.y - height },
      { x: position.x - width / 2, y: position.y - height },
    ];
  });

  const points = memberPoints.concat(bearingPoints);

  if (!points.length) return null;

  const componentBounds = getBoundsFromPoints(points);

  if (!includeDimensions) return componentBounds;

  // Note that we assume dimensions are the outermost things.
  if (dimensionBounds) return { ...componentBounds, size: dimensionBounds };

  return componentBounds;
};

export const calculateActiveComponentMembersMinMax = createSelector(
  getActiveComponent,

  (component) => {
    if (!component) return null;
    const points = component.members.flatMap((member) => calculateMemberGeometry(member, component)).filter(notNullish);
    const xValues = points.map((point) => point.x);
    const yValues = points.map((point) => point.y);
    return {
      minX: Math.min(...xValues),
      maxX: Math.max(...xValues),
      minY: Math.min(...yValues),
      maxY: Math.max(...yValues),
    };
  },
);

export function getFontSizeFromComponentBoundingBox(boundingBox: SceneObjectBounds | null) {
  const minimumFontSize = 3;
  const maximumFontSize = 10;
  if (!boundingBox) return minimumFontSize;
  const greaterDimension = Math.max(boundingBox.size.width, boundingBox.size.height);
  // 960 (80 feet) is the largest table at Clearspan, which gives us some idea of the maximum size buildable truss.
  const fontSizeScaledForComponentSize =
    minimumFontSize + (greaterDimension / 960) * (maximumFontSize - minimumFontSize);
  return Math.min(fontSizeScaledForComponentSize, maximumFontSize);
}

export const calculateBearingBounds = (component: Component, bearing: Bearing) => {
  const bearingVisualData = getBearingVisualData(component, bearing);
  if (!bearingVisualData) return null;
  const { position, width, height } = bearingVisualData;
  const { x, y } = position;
  const min = { x: x - width / 2, y: y - height };
  const max = { x: x + width / 2, y };
  return getBoundsFromMinMax(min, max);
};

export const calculatePanelBounds = (analogModel: AnalogModel, panel: AnalogPanel) => {
  const associatedLinks = analogModel.analogLinks.filter((analogLink) => panel.linkGuids.includes(analogLink.guid));
  const startAndEndPoints = getPanelStartAndEndPoints(associatedLinks);
  if (!startAndEndPoints) return calculateBoundsForPoint({ x: 50, y: 50, z: 0 });
  const { start, end } = startAndEndPoints;
  return getBoundsFromMinMax(start, end);
};

export const calculatePlatePairBounds = (platePair: PlatePair) => {
  const boundingBox = new Box3(
    new Vector3(-platePair.length / 2, -platePair.width / 2, 0),
    new Vector3(platePair.length / 2, platePair.width / 2, 0),
  );

  const euler = new Euler(
    convertDegreesToRadians(platePair.angleX),
    convertDegreesToRadians(platePair.angleY),
    convertDegreesToRadians(platePair.angleZ),
  );

  boundingBox.applyMatrix4(
    new Matrix4()
      .makeTranslation(platePair.centerX, platePair.centerY, platePair.centerZ)
      .multiply(new Matrix4().makeRotationFromEuler(euler)),
  );

  return getBoundsFromMinMax(boundingBox.min, boundingBox.max);
};

const calculateBoundsForPoint = (point: Point3D) => {
  const min = new Vector3(point.x, point.y, point.z).add(new Vector3(-1, -1, 0));
  const max = new Vector3(point.x, point.y, point.z).add(new Vector3(1, 1, 0));
  return getBoundsFromMinMax(min, max);
};

export const calculateAnalogJointBounds = (joint: AnalogJoint) => calculateBoundsForPoint(joint);

export const calculateAnalogLinkBounds = (link: AnalogLink) => {
  const i = { ...link.iJoint };
  const j = { ...link.jJoint };
  const centroid = new Vector3(i.x, i.y, i.z);
  centroid.add(new Vector3(j.x, j.y, j.z));
  centroid.multiplyScalar(0.5);
  const box = new Box3(new Vector3(0, 0, 0), new Vector3(0, 0, 0));
  box.setFromArray([i.x, i.y, i.z, j.x, j.y, j.z]);
  return getBoundsFromMinMax(box.min, box.max);
};

export const calculateReferenceLineBounds = (line: Line2D) =>
  calculateBoundsForPoint({
    x: line.basePoint.x,
    y: line.basePoint.y,
    z: 0,
  });

export const calculateProfileJointBounds = (profileJoint: ProfileJoint) =>
  calculateBoundsForPoint({
    ...profileJoint.location,
    z: 0,
  });

function calculateLevelBounds(
  levelGuid: string,
  levels: ReadonlyRecord<string, Level>,
  roofContainers: ReadonlyRecord<string, RoofContainer>,
  bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
) {
  const level = levels[levelGuid];
  if (!level) return BLANK_PROJECT_BOUNDS;

  const relevantRoofContainers = Object.values(roofContainers).filter((container) => container.levelGuid === levelGuid);
  const relevantBearingEnvelopes = Object.values(bearingEnvelopes).filter(
    (envelope) => envelope.levelGuid === levelGuid,
  );

  const verticesForLevel = [
    ...relevantRoofContainers.flatMap((container) => container.solidData?.vertices ?? []),
    ...relevantBearingEnvelopes.flatMap((envelope) => {
      const shape = calculateBearingEnvelopeShape(envelope)?.vertices;
      if (shape == null) return [];

      return shape.flatMap((vertex) => [
        { ...vertex, z: envelope.top },
        { ...vertex, z: envelope.bottom },
      ]);
    }),
  ];

  const maxHeight = max([level.plateElevation, ...verticesForLevel.map((vertex) => vertex.z)]) ?? 0;
  const desiredHeight = maxHeight + DESIRED_HEIGHT_ABOVE_BUILDING;

  return verticesForLevel.length >= 2
    ? { ...getBoundsFromPoints(verticesForLevel), desiredHeight }
    : { ...BLANK_PROJECT_BOUNDS, desiredHeight };
}

// our perspective camera does not currently respect the size property and can not currently determine a correct height,
//   so these hardcoded values are used to achieve a slightly more reasonable position

const DESIRED_HEIGHT_ABOVE_BUILDING = 300;
const DESIRED_HEIGHT_ABOVE_TRUSS = 144;

const NO_BOUNDS = {
  position: { x: 0, y: 0 },
  size: { width: 0, height: 0 },
};

const BLANK_PROJECT_BOUNDS = {
  position: { x: 12 * 10, y: 12 * 10 },
  size: { width: 12 * 20, height: 12 * 20 },
};

function calculateTwoDimensionalSelectableEntryBounds(
  entry: SelectableItemEntry,
  activeComponentDesign: ComponentDesign | null,
  activeProfile: BasicProfile | null,
  activeComponentDesignReferenceLines: readonly CalculatedLine[] | null,
  roofContainers: ReadonlyRecord<string, RoofContainer>,
  dependencies: TrussSolidCalculationDependencies,
  bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
  showDimensionsForSelectedItem: boolean,
  dimensionBounds: DimensionBounds | null,
  buildings: ReadonlyRecord<string, Building>,
  levels: ReadonlyRecord<string, Level>,
  insertions: Insertion[],
  inLayoutMode: boolean,
) {
  if (entry.type == null) {
    throw new Error('Selected item type is not known.');
  }

  // Unsupported entries:
  switch (entry.type) {
    case 'splice':
    case 'chase':
    case 'zone':
    case 'endCondition':
    case 'run':
      return NO_BOUNDS;
  }

  // Entries that don't depend on an active component design:
  switch (entry.type) {
    case 'analogJoint':
      return calculateAnalogJointBounds(entry.item);

    case 'analogLink':
      return calculateAnalogLinkBounds(entry.item);

    case 'beam':
    case 'wall': {
      const bearingPolygon = calculateBearingEnvelopeShape(entry.item);
      if (bearingPolygon == null) return;

      return {
        ...getBoundsFromPoints(bearingPolygon.vertices),
        desiredHeight: entry.item.top + DESIRED_HEIGHT_ABOVE_BUILDING,
      };
    }

    case 'componentDesign':
      return calculateComponentBounds(
        entry.item.component,
        !inLayoutMode && showDimensionsForSelectedItem,
        dimensionBounds,
      );

    case 'roofContainer': {
      const roofContainer = roofContainers[entry.item.guid];
      const maxHeight = max(
        Object.values(roofContainers).flatMap(({ solidData }) => solidData?.vertices.map(({ z }) => z) ?? []),
      )!;

      if (roofContainer && roofContainer.solidData) {
        return {
          ...getBoundsFromPoints(roofContainer.solidData.vertices),
          desiredHeight: maxHeight + DESIRED_HEIGHT_ABOVE_BUILDING,
        };
      }

      break;
    }

    case 'project':
      const allVertices = [
        ...Object.values(roofContainers).flatMap((container) => container.solidData?.vertices ?? []),
        ...Object.values(bearingEnvelopes).flatMap((envelope) => {
          const shape = calculateBearingEnvelopeShape(envelope)?.vertices;
          if (shape == null) return [];

          return shape.flatMap((vertex) => [
            { ...vertex, z: envelope.top },
            { ...vertex, z: envelope.bottom },
          ]);
        }),
      ];

      if (allVertices.length >= 2) {
        const maxHeight = max(allVertices.map((vertex) => vertex.z))!;
        return {
          ...getBoundsFromPoints(allVertices),
          desiredHeight: maxHeight + DESIRED_HEIGHT_ABOVE_BUILDING,
        };
      }

      return BLANK_PROJECT_BOUNDS;

    case 'building': {
      const building = buildings[entry.item.guid];
      if (building == null) return BLANK_PROJECT_BOUNDS;

      const levelGuids = new Set(Object.keys(levels));
      const levelInsertions = insertions
        .filter((insertion) => insertion.parentGuid === building.guid)
        .filter((insertion) => levelGuids.has(insertion.objectGuid));

      if (levelInsertions.length === 0) return BLANK_PROJECT_BOUNDS;

      return levelInsertions
        .map((insertion) => calculateLevelBounds(insertion.objectGuid, levels, roofContainers, bearingEnvelopes))
        .reduce(mergeBounds);
    }

    case 'level':
      return calculateLevelBounds(entry.item.guid, levels, roofContainers, bearingEnvelopes);

    case 'platePair':
      return calculatePlatePairBounds(entry.item);

    case 'profileJoint':
      return calculateProfileJointBounds(entry.item);

    case 'trussEnvelope': {
      const shape = calculateTrussEnvelopeShape(entry.item);

      if (shape == null) {
        return NO_BOUNDS;
      }

      const resultBounds = getBoundsFromPoints(shape.vertices);

      const trussSolid = calculateTrussSolidWithCache(entry.item, dependencies).ok;

      // envelope elevation is not the best replacement for truss height if the solid fails to calculate, but it at
      //   least ensures the camera will be positioned with the truss visible
      const trussHeight = trussSolid ? max(trussSolid.vertices.map((vertex) => vertex.z))! : entry.item.elevation;

      resultBounds.desiredHeight = trussHeight + DESIRED_HEIGHT_ABOVE_TRUSS;

      return resultBounds;
    }
  }

  if (activeComponentDesign == null) return;

  // Entries that depend on an active component design:
  switch (entry.type) {
    case 'productionComponentInstance':
      return calculateComponentBounds(
        activeComponentDesign.component,
        !inLayoutMode && showDimensionsForSelectedItem,
        dimensionBounds,
      );

    case 'member':
    case 'brace':
      return calculateMemberBounds(activeComponentDesign.component, entry.item);

    case 'loadCase':
    case 'load':
      return calculateComponentBounds(
        activeComponentDesign.component,
        inLayoutMode && showDimensionsForSelectedItem,
        dimensionBounds,
      );

    case 'bearing':
      return calculateBearingBounds(activeComponentDesign.component, entry.item);

    case 'panel':
      return calculatePanelBounds(activeComponentDesign.analogModel!, entry.item);

    case 'referenceLine': {
      if (!activeComponentDesignReferenceLines) return null;
      const component = { ...activeComponentDesign.component, referenceLines: activeComponentDesignReferenceLines };
      const calculatedLine = calculateLine(entry.item, component);
      return calculateReferenceLineBounds(calculatedLine!);
    }

    case 'webJoint': {
      if (!activeProfile) throw new Error();
      const webJointLocation = findAndCalculateWebJointLocation(activeProfile, entry.item.guid);
      return calculateBoundsForPoint({ x: webJointLocation.x, y: webJointLocation.y, z: 0 });
    }

    case 'webJointSegment':
      const profileLine = activeProfile?.profileLines.find((line) =>
        line.webJointSegments.some(({ guid }) => guid === entry.item.guid),
      );
      if (!profileLine) throw new Error();
      const geometry = calculateWebJointSegmentGeometry(activeProfile!, profileLine, entry.item);
      return getBoundsFromPoints(geometry.endPoints);
  }
}

export const getActiveComponentDesignCenter = createSelector(
  getActiveComponentDesign,
  (state: RootState) => state.visualOptions.showDimensionsForSelectedItem,
  (design, showDimensions) =>
    design == null ? null : (calculateComponentBounds(design.component, showDimensions, null)?.position ?? null),
);

// A factor to control how far away default dimensions are drawn from the component.
const EXPLOSION_DISTANCE = 12;

// The additional distance that will be added if we think the default dimension will have a large number of labels and
//   thus require more space.
const ADDITIONAL_EXPLOSION_DISTANCE = EXPLOSION_DISTANCE;

export const getActiveDimensionData = createSelector(
  getActiveDimensionSet,
  getActiveComponent,
  getActiveComponentDesignCenter,
  (state: RootState) => state.visualOptions.visibleDimensionCategory,
  (state: RootState) => state.visualOptions.showStandardDimensions,
  (state: RootState) => state.visualOptions.showDimensionsForSelectedItem,
  (
    dimensionSet,
    component,
    componentCenter,
    visibleDimensionCategory,
    showStandardDimensions,
    showDimensionsForSelectedItem,
  ) => {
    if (!componentCenter || !component || !dimensionSet || !showStandardDimensions || !showDimensionsForSelectedItem)
      return null;

    let numberOfVerticalDimensionSetsLeft = 0;
    let numberOfVerticalDimensionSetsRight = 0;
    let numberOfHorizontalDimensionSetsBottom = 0;
    let numberOfHorizontalDimensionSetsTop = 0;
    let additionalExplosionDistanceLeft = 0;
    let additionalExplosionDistanceRight = 0;
    let additionalExplosionDistanceBottom = 0;
    let additionalExplosionDistanceTop = 0;
    const visiblePointsOnLines: Point2DInterface[][] = [];

    return dimensionSet.distanceDimensionLines
      .map((dimensionLine, index) => {
        const firstPoint = dimensionLine.points[0];
        const lastPoint = dimensionLine.points[dimensionLine.points.length - 1];

        const isVertical = firstPoint.x.toFixed(4) === lastPoint.x.toFixed(4);
        const isHorizontal = firstPoint.y.toFixed(4) === lastPoint.y.toFixed(4);

        const candidateForPushing = !dimensionLine.isContextDependent;

        let shift = new Point2D(0, 0);
        let zeroOnLine: Point2D | undefined;

        const distanceBetweenPoints: number[] = [];
        for (let i = 0; i < dimensionLine.points.length - 2; i += 1) {
          const currentPoint = dimensionLine.points[i];
          const nextPoint = dimensionLine.points[i + 1];
          const aSquared = (nextPoint.x - currentPoint.x) ** 2;
          const bSquared = (nextPoint.y - currentPoint.y) ** 2;
          distanceBetweenPoints.push(Math.sqrt(aSquared + bSquared));
        }

        if (candidateForPushing) {
          if (isVertical) {
            const isRightSide = firstPoint.x > componentCenter.x;
            if (isRightSide) {
              numberOfVerticalDimensionSetsRight += 1;
              additionalExplosionDistanceRight += ADDITIONAL_EXPLOSION_DISTANCE;
              shift = new Point2D(
                EXPLOSION_DISTANCE * numberOfVerticalDimensionSetsRight + additionalExplosionDistanceRight,
                0,
              );
            } else {
              numberOfVerticalDimensionSetsLeft += 1;
              additionalExplosionDistanceLeft += ADDITIONAL_EXPLOSION_DISTANCE;
              shift = new Point2D(
                -EXPLOSION_DISTANCE * numberOfVerticalDimensionSetsLeft - additionalExplosionDistanceLeft,
                0,
              );
            }
            zeroOnLine = dimensionLine.points.find((point) => distanceEquals(point.y, 0))
              ? new Point2D(firstPoint.x, 0)
              : new Point2D(firstPoint.x, firstPoint.y);
          } else if (isHorizontal) {
            const isTop = firstPoint.y > componentCenter.y;
            if (isTop) {
              numberOfHorizontalDimensionSetsTop += 1;
              additionalExplosionDistanceTop += ADDITIONAL_EXPLOSION_DISTANCE;
              shift = new Point2D(
                0,
                EXPLOSION_DISTANCE * numberOfHorizontalDimensionSetsTop + additionalExplosionDistanceTop,
              );
            } else {
              numberOfHorizontalDimensionSetsBottom += 1;
              additionalExplosionDistanceBottom += ADDITIONAL_EXPLOSION_DISTANCE;
              shift = new Point2D(
                0,
                -EXPLOSION_DISTANCE * numberOfHorizontalDimensionSetsBottom - additionalExplosionDistanceBottom,
              );
            }
            zeroOnLine = dimensionLine.points.find((point) => distanceEquals(point.x, 0))
              ? new Point2D(0, firstPoint.y)
              : new Point2D(firstPoint.x, firstPoint.y);
          } else {
            const isTop = firstPoint.y > componentCenter.y;
            shift = Direction2D.fromPointObjects(firstPoint, lastPoint)
              .rotateQuarterTurn()
              .toVector(isTop ? EXPLOSION_DISTANCE : -EXPLOSION_DISTANCE).imageOfOrigin;
          }
        }

        if (zeroOnLine) zeroOnLine = zeroOnLine.add(shift);

        const visibleDimensionPoints = dimensionLine.points.filter((point) =>
          point.categories.includes(visibleDimensionCategory),
        );

        // If the visible points for the line we're about to create match an already existing line exactly, there is no
        // need to create it.
        if (
          visiblePointsOnLines.some(
            (points) =>
              points.length === visibleDimensionPoints.length &&
              points.every((point) =>
                visibleDimensionPoints.some(
                  (newDimensionPoint) => newDimensionPoint.x === point.x && newDimensionPoint.y === point.y,
                ),
              ),
          )
        )
          return null;

        visiblePointsOnLines.push(visibleDimensionPoints);
        return {
          key: index,
          dimensionPoints: visibleDimensionPoints.map((point) => Point2D.fromObject(point).add(shift)),
          zeroOnLine,
        };
      })
      .filter(notNullish);
  },
);

export interface DimensionBounds {
  readonly width: number;
  readonly height: number;
}

export const getActiveDimensionBounds = createSelector(
  getActiveDimensionData,
  (dimensionData): DimensionBounds | null => {
    if (!dimensionData) return null;

    const { max, min } = new Box3().setFromPoints(
      dimensionData.flatMap(({ dimensionPoints }) => dimensionPoints).map(({ x, y }) => new Vector3(x, y)),
    );
    return { width: max.x - min.x, height: max.y - min.y };
  },
);

export const getBoundsOfActiveComponentDesign = createSelector(
  getActiveComponentDesign,
  (state: RootState) => state.visualOptions.showDimensionsForSelectedItem,
  getActiveDimensionBounds,

  (design, showDimensions, dimensionBounds) =>
    design == null ? null : calculateComponentBounds(design.component, showDimensions, dimensionBounds),
);

export const getBoundsOfActiveComponentDesignWithoutDimensions = createSelector(
  getActiveComponentDesign,
  (design) => design && calculateComponentBounds(design.component, false, null),
);

export const getBoundsCalculator = createSelector(
  getActiveComponentDesign,
  getInsertingOrRewebbingProfileOrNull,
  getReferenceLinesForActiveComponent,
  (state: RootState) => state.layout.roofContainers,
  getTrussSolidCalculationDependencies,
  (state: RootState) => state.layout.bearingEnvelopes,
  (state: RootState) => state.visualOptions.showDimensionsForSelectedItem,
  getActiveDimensionBounds,
  getAllBuildingsDictionary,
  getAllLevelsDictionary,
  getInsertionsWithPendingChanges,
  isLayoutCameraInUse,

  (
    activeComponentDesign,
    activeProfile,
    referenceLines,
    roofContainers,
    dependencies,
    bearingEnvelopes,
    showDimensionsForSelectedItem,
    dimensionBounds,
    buildings,
    levels,
    insertions,
    inLayoutMode,
  ) =>
    (entry: SelectableItemEntry | null) =>
      entry
        ? calculateTwoDimensionalSelectableEntryBounds(
            entry,
            activeComponentDesign,
            activeProfile,
            referenceLines,
            roofContainers,
            dependencies,
            bearingEnvelopes,
            showDimensionsForSelectedItem,
            dimensionBounds,
            buildings,
            levels,
            insertions,
            inLayoutMode,
          )
        : null,
);

export const getSingleSelectedItemBoundsOrNull = createSelector(
  getSingleSelectedItemEntryOrNull,
  getBoundsCalculator,

  (selectedEntry, calculateEntryBounds) => {
    if (!selectedEntry) return null;

    const result = calculateEntryBounds(selectedEntry);

    if (!result) return null;
    if (result.size.width === 0 || result.size.height === 0) {
      console.warn('Bounding box height or width is 0.');
    }

    return result;
  },
);

export const getAllSelectedItemsBoundsOrNull = createSelector(
  getSelectedItemEntries,
  getBoundsCalculator,

  (selectedEntries, calculateEntryBounds) => {
    if (selectedEntries.length === 0) return null;

    const allBounds = selectedEntries.map(calculateEntryBounds).filter(notNullish);
    if (allBounds.length === 0) return null;

    return allBounds.reduce(mergeBounds);
  },
);

export const getInsertionProfileBoundsOrNull = createSelector(
  getInsertingProfileTrussSettingsOrNull,
  (profileTrussSettings) => {
    if (profileTrussSettings == null) return null;

    return {
      position: { x: profileTrussSettings.span / 2, y: 0 },
      size: { width: profileTrussSettings.span, height: 0 },
    };
  },
);
